import {CancelToken} from 'axios';
import {format} from 'date-fns';

import {apiClient} from 'core/apiClient';
import {profile$} from 'models/auth';
import {billingAccess$} from 'models/profile/billing/access';

import {
    BillingBalance,
    BillingDebitRequest,
    BillingHistoryRequest,
    BillingInvoicePageableContent,
    BillingPageableContent,
    BillingRawRow,
    ChangeInvoiceStatusRequest,
} from '../types/billing';

const URL = '/service/billing';

export async function fetchBillingHistory(payload: BillingHistoryRequest) {
    const {legalIdentityId, allOrganizations, ...params} = payload;
    const access = billingAccess$.getState();
    if (allOrganizations ? !access.canViewAllTransactions : !access.canViewOwnTransactions)
        throw new Error('Billing access denied.');
    if (
        !allOrganizations &&
        (!legalIdentityId || legalIdentityId !== profile$.getState()?.client?.legalIdentityId)
    )
        throw new Error('Own legalIdentityId is required.');
    return (
        await apiClient.post<BillingPageableContent<BillingRawRow>>(
            allOrganizations ? `${URL}/history` : `${URL}/history/${legalIdentityId}`,
            params,
        )
    ).data;
}

export async function getCurrentBilling(id?: number) {
    if (!billingAccess$.getState().canReadOwnBalance) throw new Error('Billing access denied.');
    if (!id || id !== profile$.getState()?.client?.legalIdentityId)
        throw new Error('Own legalIdentityId is required.');
    return (await apiClient.get<number>(`${URL}/balance/${id}`)).data;
}

export async function getTotalBilling() {
    if (!billingAccess$.getState().canViewAllTransactions)
        throw new Error('Billing access denied.');
    return (await apiClient.get<number>(`${URL}/balance`)).data;
}

export async function depositById(body: BillingDebitRequest) {
    return (await apiClient.post<BillingBalance>(`${URL}/debit`, body)).data;
}

export interface PurchaseMarkingCodesRequest {
    quantity: number;
    email: string;
}

export interface PurchaseMarkingCodesResponse {
    blob: Blob;
    filename: string;
}

export async function purchaseMarkingCodes(
    body: PurchaseMarkingCodesRequest,
    cancelToken?: CancelToken,
): Promise<PurchaseMarkingCodesResponse> {
    if (!billingAccess$.getState().canRequestInvoice) throw new Error('Billing access denied.');
    if (!Number.isInteger(body.quantity) || body.quantity <= 0 || body.quantity > 2147483647)
        throw new Error('A positive 32-bit integer quantity is required.');
    const res = await apiClient.post<Blob>(`${URL}/invoice/generate`, body, {
        responseType: 'blob',
        cancelToken,
    });

    const contentDisposition =
        res.headers['content-disposition'] || res.headers['Content-Disposition'];
    let filename = `invoice-${Date.now()}.pdf`;

    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
            try {
                filename = decodeURIComponent(filename);
            } catch (e) {
                console.error(e);
            }
        }
    }

    return {
        blob: res.data,
        filename,
    };
}

export async function fetchBillingInvoices(params: Omit<BillingHistoryRequest, 'legalIdentityId'>) {
    if (!billingAccess$.getState().canViewInvoices) throw new Error('Billing access denied.');
    const {sortings, paging, filters, searchById, searchByTin, searchByIrn} = params;
    const sort = sortings?.length
        ? sortings.reduce<string[]>((result, sorting) => {
              const field = sorting.id === 'tin' ? 'client.tin' : sorting.id;
              const direction = sorting.desc ? 'desc' : 'asc';

              result.push(`${field},${direction}`);
              if (sorting.id === 'issueDate') result.push(`issueTime,${direction}`);

              return result;
          }, [])
        : ['issueDate,desc', 'issueTime,desc'];
    const issueDate = filters?.find((filter) => filter.id === 'issueDate')?.value[0];
    const formattedDate = issueDate ? format(new Date(issueDate as string), 'yyyy-MM-dd') : null;
    const status = filters?.find((filter) => filter.id === 'status')?.value[0] || null;
    // const tin = filters?.find((filter) => filter.id === 'tin')?.value[0] || null;
    const {pageNumber, pageSize} = paging;

    const setParams = {
        status,
        issueDate: formattedDate,
        page: pageNumber,
        size: pageSize,
        sort,
        id: searchById,
        tin: searchByTin,
        irn: searchByIrn,
    };

    return (await apiClient.post<BillingInvoicePageableContent>(`${URL}/invoice`, setParams)).data;
}

export async function changeInvoiceStatus(params: ChangeInvoiceStatusRequest): Promise<void> {
    if (!billingAccess$.getState().canUpdatePayment) throw new Error('Billing access denied.');
    if (!params.docNumber || !params.irn || !params.email || !params.tin) {
        throw new Error('Invoice payment details are required.');
    }

    await apiClient.post(`${URL}/invoice/set-paid`, params);
}

export async function downloadInvoice(id: string) {
    if (!billingAccess$.getState().canRequestInvoice) throw new Error('Billing access denied.');
    return apiClient.post<Blob>(`${URL}/invoice/${encodeURIComponent(id)}/download`, null, {
        responseType: 'blob',
    });
}
